import {
  type CreateIssueRequest,
  type Issue,
  updateIssueRequestSchema,
} from "@qualtio-workhub/contracts";
import {
  Avatar,
  Badge,
  Button,
  Card,
  Input,
  LoadingState,
  Select,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@qualtio-workhub/ui";
import { useIsMobile } from "@qualtio-workhub/ui/hooks/use-mobile";
import { useParams, useRouter, useRouterState } from "@tanstack/react-router";
import {
  AlertCircle,
  ArrowLeft,
  Bot,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Database,
  ExternalLink,
  GitPullRequest,
  History,
  Play,
  Send,
  XCircle,
} from "lucide-react";
import { type FormEvent, type ReactNode, useCallback, useRef, useState } from "react";
import {
  addIssueComment,
  assignIssueAgent,
  assignIssueUser,
  attachIssueArtifact,
  changeIssueStatus,
  createAgentRun,
  deleteIssue,
  getIssueDetail,
  isForbiddenError,
  linkGithubPullRequest,
  suggestGithubBranch,
  updateAgentRun,
  updateIssue,
} from "../../shared/api/client";
import { useWorkhubQuery } from "../../shared/api/hooks";
import { useAuth } from "../../shared/auth/auth-provider";
import { QueryFeedback } from "../../shared/components/query-feedback";
import { formatUtcIsoForDateTimeInput, parseDateTimeInputToUtcIso } from "../../shared/date-time";
import { useI18n } from "../../shared/i18n/provider";
import {
  type AgentRunTransitionStatus,
  buildAgentRunTransitionPayload,
  getAgentRunActionViews,
} from "../agents/agent-run-actions";
import { EditIssueModal } from "./edit-issue-modal";
import { getGithubPullRequestUrlError } from "./github-pr-url";
import { activityLabel } from "./issue-activity";
import { IssueInlineField } from "./issue-inline-field";
import { issueListHref, readIssueListSearch } from "./issue-list-model";
import { IssueLabelEditor, IssuePropertySelect } from "./issue-property-editors";
import { formatDate, IssueKey, PriorityBadge, statusChangePayload } from "./issue-widgets";
import { MentionTextarea } from "./mention-textarea";
import {
  formatIssueReference,
  type IssueStatus,
  priorityLabelKeys,
  priorityOptions,
  statusLabelKeys,
  statusOptions,
  typeLabelKeys,
  typeOptions,
} from "./model";
import { useIssueReferenceData } from "./reference-data";
import { UserAutocomplete } from "./user-autocomplete-input";

export function CommentSubmitButton({ disabled }: { disabled: boolean }) {
  const { t } = useI18n();

  return (
    <Button
      aria-label={t("issue.action.submitComment")}
      disabled={disabled}
      size="icon"
      type="submit"
    >
      <Send aria-hidden="true" size={16} />
    </Button>
  );
}

function PanelHeader({ icon, label, meta }: { icon: ReactNode; label: string; meta: string }) {
  return (
    <div className="issue-section-heading">
      <h2 className="icon-title">
        {icon}
        {label}
      </h2>
      <small>{meta}</small>
    </div>
  );
}

function KeyValueRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="issue-property-row">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function AgentRunActionIcon({ actionId }: { actionId: string }) {
  if (actionId === "mark_running") return <Play size={16} />;
  if (actionId === "request_human") return <AlertCircle size={16} />;
  if (actionId === "complete") return <CheckCircle2 size={16} />;
  return <XCircle size={16} />;
}

export function IssueDetailPage() {
  const params = useParams({ strict: false });
  const issueKey = String(params["issueKey"] ?? "");
  const router = useRouter();
  const searchStr = useRouterState({ select: (state) => state.location.searchStr });
  const listSearch = readIssueListSearch(searchStr);
  const backHref = issueListHref(listSearch);
  const isMobile = useIsMobile();
  const auth = useAuth();
  const { t } = useI18n();
  const loadDetail = useCallback((token: string) => getIssueDetail(token, issueKey), [issueKey]);
  const detailQuery = useWorkhubQuery(loadDetail, [issueKey]);
  const references = useIssueReferenceData(issueKey);
  const [comment, setComment] = useState("");
  const [runGoal, setRunGoal] = useState("");
  const [runNote, setRunNote] = useState("");
  const [prUrl, setPrUrl] = useState("");
  const [prTouched, setPrTouched] = useState(false);
  const [branchName, setBranchName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [deleteArmed, setDeleteArmed] = useState(false);
  const [activityTab, setActivityTab] = useState("all");
  const [confirmedIssue, setConfirmedIssue] = useState<Issue | null>(null);
  const [savingField, setSavingField] = useState(false);
  const [saveAnnouncement, setSaveAnnouncement] = useState("");
  const saveLock = useRef(false);
  const activeIssueKey = useRef(issueKey);
  activeIssueKey.current = issueKey;
  const detail = detailQuery.data?.issue.key === issueKey ? detailQuery.data : null;
  const isAdmin = auth.user?.role === "admin";

  async function saveIssueFields(patch: Partial<CreateIssueRequest>) {
    if (auth.token === null || saveLock.current) throw new Error("issue_save_unavailable");
    const input = updateIssueRequestSchema.parse(patch);
    const field = Object.keys(input).length === 1 ? Object.keys(input)[0] : undefined;
    if (!isAdmin && !["status", "assigneeUserId", "assigneeAgentId"].includes(field ?? ""))
      throw new Error("issue_save_forbidden");
    saveLock.current = true;
    const focusedControl =
      document.activeElement instanceof HTMLElement &&
      ["detail-status", "detail-agent", "detail-owner"].includes(document.activeElement.id)
        ? document.activeElement
        : null;
    setSavingField(true);
    setSaveAnnouncement("");
    try {
      const saved =
        field === "status" && input.status !== undefined
          ? await changeIssueStatus(auth.token, issueKey, statusChangePayload(input.status))
          : field === "assigneeUserId" && input.assigneeUserId !== undefined
            ? await assignIssueUser(auth.token, issueKey, { assigneeUserId: input.assigneeUserId })
            : field === "assigneeAgentId" && input.assigneeAgentId !== undefined
              ? await assignIssueAgent(auth.token, issueKey, {
                  assigneeAgentId: input.assigneeAgentId,
                })
              : await updateIssue(auth.token, issueKey, input);
      if (activeIssueKey.current === issueKey) {
        setConfirmedIssue(saved);
        setSaveAnnouncement(t("issue.inline.saved"));
        await detailQuery.refetch();
      }
    } finally {
      saveLock.current = false;
      setSavingField(false);
      if (focusedControl)
        requestAnimationFrame(() => {
          if (
            focusedControl.isConnected &&
            (document.activeElement === document.body || document.activeElement === focusedControl)
          )
            focusedControl.focus({ preventScroll: true });
        });
    }
  }

  async function onCommentSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (auth.token === null || comment.trim().length === 0) {
      return;
    }
    setError(null);
    try {
      await addIssueComment(auth.token, issueKey, { body: comment });
      setComment("");
      await detailQuery.refetch();
    } catch {
      setError(t("issue.comment.error"));
    }
  }

  async function updateStatus(status: IssueStatus) {
    if (auth.token === null) {
      return;
    }
    setError(null);
    try {
      await saveIssueFields({ status });
    } catch {
      setError(t("issue.status.error"));
    }
  }

  async function updateOwner(assigneeUserId: string | null) {
    if (auth.token === null) {
      return;
    }
    setError(null);
    try {
      await saveIssueFields({ assigneeUserId });
    } catch {
      setError(t("issue.owner.error"));
    }
  }

  async function updateAgent(assigneeAgentId: string | null) {
    if (auth.token === null) {
      return;
    }
    setError(null);
    try {
      await saveIssueFields({ assigneeAgentId });
    } catch {
      setError(t("issue.agent.error"));
    }
  }

  async function startRun() {
    if (
      auth.token === null ||
      detail === null ||
      issue.assigneeAgentId === null ||
      !runGoal.trim()
    ) {
      return;
    }
    setError(null);
    try {
      await createAgentRun(auth.token, {
        agentId: issue.assigneeAgentId,
        goal: runGoal,
        issueId: issue.id,
        source: "web",
      });
      setRunGoal("");
      await detailQuery.refetch();
    } catch {
      setError(t("agents.runStartError"));
    }
  }

  async function updateLatestRun(status: AgentRunTransitionStatus) {
    if (auth.token === null || latestRun === undefined) return;
    const body = buildAgentRunTransitionPayload(status, runNote, t);
    setError(null);
    try {
      await updateAgentRun(auth.token, latestRun.id, body);
      setRunNote("");
      await detailQuery.refetch();
    } catch {
      setError(t("agents.runUpdateError"));
    }
  }

  async function attachRunArtifact() {
    if (auth.token === null || latestRun === undefined) return;
    setError(null);
    try {
      await attachIssueArtifact(auth.token, issueKey, {
        agentRunId: latestRun.id,
        body: runNote || null,
        title: runNote || t("agents.artifactTitle"),
        type: "note",
        url: null,
      });
      setRunNote("");
      await detailQuery.refetch();
    } catch {
      setError(t("agents.artifactError"));
    }
  }

  async function suggestBranch() {
    if (auth.token === null || detail === null) return;
    setError(null);
    try {
      setBranchName(
        await suggestGithubBranch(auth.token, { issueKey: issue.key, title: issue.title }),
      );
    } catch {
      setError(t("github.branchSuggestError"));
    }
  }

  async function linkPr() {
    const prErrorKey = getGithubPullRequestUrlError(prUrl);
    if (auth.token === null || !prUrl.trim() || prErrorKey !== null) {
      setPrTouched(true);
      return;
    }
    setError(null);
    try {
      await linkGithubPullRequest(auth.token, issueKey, {
        agentRunId: latestRun?.id,
        url: prUrl,
      });
      setPrUrl("");
      setPrTouched(false);
      await detailQuery.refetch();
    } catch {
      setError(t("github.prLinkError"));
    }
  }

  async function onDeleteIssue() {
    if (auth.token === null || !deleteArmed) {
      setDeleteArmed(true);
      return;
    }

    setError(null);
    try {
      await deleteIssue(auth.token, issueKey);
      await router.navigate({ to: "/issues", search: listSearch });
    } catch (caught) {
      setError(isForbiddenError(caught) ? t("issue.delete.forbidden") : t("issue.delete.error"));
      setDeleteArmed(false);
    }
  }

  if (detailQuery.loading && detail === null) {
    return (
      <section className="page-frame">
        <LoadingState label={t("app.loading")} />
      </section>
    );
  }

  if (detail === null) {
    return (
      <section className="page-frame">
        <Card className="work-panel">
          <p className="error-text">{t("issue.detail.notFound")}</p>
          <Button asChild variant="secondary">
            <a href={backHref}>{t("nav.issues")}</a>
          </Button>
        </Card>
      </section>
    );
  }

  // Keep the confirmed write visible if refreshing the activity feed fails.
  const issue =
    confirmedIssue?.key === issueKey &&
    Date.parse(confirmedIssue.updatedAt) > Date.parse(detail.issue.updatedAt)
      ? confirmedIssue
      : detail.issue;
  const latestRun = detail.agentRuns[0];
  const parentIssue =
    references.issues.data?.find((item) => item.id === issue.parentIssueId) ?? null;
  const actorDisplayName = (
    actorType: string,
    actorId: string | null,
    actorDisplayName?: string,
  ) => {
    if (actorDisplayName !== undefined) {
      return actorDisplayName;
    }
    if (actorType === "user") {
      return references.users.data?.find((user) => user.id === actorId)?.name ?? actorType;
    }
    if (actorType === "agent") {
      return references.agents.data?.find((agent) => agent.id === actorId)?.name ?? actorType;
    }
    return actorType;
  };
  const actorInitial = (actorType: string, actorId: string | null, displayName?: string) =>
    actorDisplayName(actorType, actorId, displayName).slice(0, 1).toUpperCase();
  const prUrlErrorKey = getGithubPullRequestUrlError(prUrl);
  const canLinkPullRequest = prUrl.trim().length > 0 && prUrlErrorKey === null;
  const latestRunActions = latestRun ? getAgentRunActionViews(latestRun.status) : [];
  const contextProperties = [
    { field: "productId", label: "issue.field.product", query: references.products },
    { field: "clientAccountId", label: "issue.field.client", query: references.clients },
    { field: "projectId", label: "issue.field.project", query: references.projects },
    { field: "chapterId", label: "issue.field.chapter", query: references.chapters },
    { field: "cycleId", label: "issue.field.cycle", query: references.cycles },
    {
      field: "researchTrackId",
      label: "issue.field.researchTrack",
      query: references.researchTracks,
    },
    {
      field: "roadmapMilestoneId",
      label: "issue.field.roadmapMilestone",
      query: references.roadmap,
    },
  ] as const;
  const feed = [
    ...(activityTab === "changes"
      ? []
      : detail.comments.map((data) => ({ kind: "comment" as const, data }))),
    ...(activityTab === "comments"
      ? []
      : detail.activity
          .filter((event) => activityTab === "changes" || event.type !== "comment.created")
          .map((data) => ({ kind: "change" as const, data }))),
  ].sort((left, right) => Date.parse(left.data.createdAt) - Date.parse(right.data.createdAt));

  return (
    <section className="page-frame work-page issue-detail-page">
      <header className="issue-detail-toolbar">
        <nav aria-label={t("issue.detail.breadcrumb")} className="issue-detail-breadcrumb">
          <Button asChild size="sm" variant="ghost">
            <a href={backHref}>
              <ArrowLeft aria-hidden="true" size={14} />
              {t("nav.issues")}
            </a>
          </Button>
          <ChevronRight aria-hidden="true" size={13} />
          <IssueKey issue={issue} />
        </nav>
        <div className="issue-detail-toolbar-actions">
          {!["in_progress", "done", "canceled"].includes(issue.status) ? (
            <Button
              disabled={savingField}
              size="sm"
              variant="secondary"
              onClick={() => void updateStatus("in_progress")}
            >
              <Play aria-hidden="true" size={14} />
              {t("issue.detail.action.start")}
            </Button>
          ) : null}
          {isAdmin ? (
            <Button
              disabled={savingField}
              size="sm"
              variant="secondary"
              onClick={() => setEditing(true)}
            >
              {t("issue.action.edit")}
            </Button>
          ) : null}
        </div>
      </header>
      <span className="sr-only" role="status">
        {saveAnnouncement}
      </span>
      {error ? (
        <p className="error-text" role="alert">
          {error}
        </p>
      ) : null}
      {detailQuery.error ? (
        <QueryFeedback
          loading={detailQuery.loading}
          error={detailQuery.error}
          onRetry={detailQuery.refetch}
          emptyTitle={t("issue.detail.notFound")}
        />
      ) : null}
      <div className="issue-detail-layout">
        <article className="issue-document" key={issue.id}>
          <IssueInlineField
            disabled={savingField}
            editable={isAdmin}
            field="title"
            onSave={(title) => saveIssueFields({ title })}
            value={issue.title}
          />
          <IssueInlineField
            disabled={savingField}
            displayValue={issue.description || t("issue.noDescription")}
            editable={isAdmin}
            field="description"
            onSave={(description) => saveIssueFields({ description })}
            value={issue.description ?? ""}
          />
        </article>
        <aside
          aria-label={t("issue.detail.properties")}
          className="issue-properties"
          key={`properties-${issue.id}`}
        >
          <h2>{t("issue.detail.properties")}</h2>
          <div className="issue-property-controls">
            <label className="field" htmlFor="detail-status">
              <span>{t("issue.field.status")}</span>
              <Select
                disabled={savingField}
                id="detail-status"
                value={issue.status}
                onChange={(event) => void updateStatus(event.currentTarget.value as IssueStatus)}
              >
                {statusOptions.map((status) => (
                  <option key={status} value={status}>
                    {t(statusLabelKeys[status])}
                  </option>
                ))}
              </Select>
            </label>
            <IssuePropertySelect
              disabled={savingField}
              displayValue={<PriorityBadge priority={issue.priority} />}
              editable={isAdmin}
              field="priority"
              label={t("issue.field.priority")}
              onSave={(priority) => saveIssueFields({ priority: priority as Issue["priority"] })}
              options={priorityOptions.map((priority) => ({
                id: priority,
                name: t(priorityLabelKeys[priority]),
              }))}
              value={issue.priority}
            />
            <fieldset className="field issue-owner-field" disabled={savingField}>
              <label htmlFor="detail-owner">{t("issue.field.assigneeUser")}</label>
              <UserAutocomplete
                clearLabel={t("issue.owner.clear")}
                id="detail-owner"
                noResultsLabel={t("issue.userAutocomplete.noResults")}
                placeholder={t("issue.owner.placeholder")}
                selectedUserId={issue.assigneeUserId}
                users={references.users.data ?? []}
                onSelectedUserIdChange={(userId) => void updateOwner(userId)}
              />
            </fieldset>
            <label className="field" htmlFor="detail-agent">
              <span>{t("issue.field.agent")}</span>
              <Select
                disabled={savingField}
                id="detail-agent"
                value={issue.assigneeAgentId ?? ""}
                onChange={(event) => void updateAgent(event.currentTarget.value || null)}
              >
                <option value="">{t("issue.unassigned")}</option>
                {(references.agents.data ?? []).map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name}
                  </option>
                ))}
              </Select>
            </label>
            <IssuePropertySelect
              disabled={savingField}
              editable={isAdmin}
              field="type"
              label={t("issue.field.type")}
              onSave={(type) => saveIssueFields({ type: type as Issue["type"] })}
              options={typeOptions.map((type) => ({ id: type, name: t(typeLabelKeys[type]) }))}
              value={issue.type}
            />
            <IssueInlineField
              disabled={savingField}
              displayValue={
                issue.dueDate
                  ? `${formatDate(issue.dueDate, "UTC")} · ${formatUtcIsoForDateTimeInput(issue.dueDate).slice(11)} UTC`
                  : t("issue.detail.noDueDate")
              }
              editable={isAdmin}
              field="dueDate"
              onSave={(value) =>
                saveIssueFields({ dueDate: value ? parseDateTimeInputToUtcIso(value) : null })
              }
              value={formatUtcIsoForDateTimeInput(issue.dueDate)}
            />
            <IssueInlineField
              disabled={savingField}
              displayValue={
                issue.estimate === null ? t("issue.unassigned") : String(issue.estimate)
              }
              editable={isAdmin}
              field="estimate"
              onSave={(value) => saveIssueFields({ estimate: value ? Number(value) : null })}
              value={issue.estimate === null ? "" : String(issue.estimate)}
            />
          </div>
          <details className="issue-context" open={!isMobile}>
            <summary>
              {t("issue.detail.context")}
              <ChevronDown aria-hidden="true" size={14} />
            </summary>
            <div className="metadata-stack">
              {contextProperties.map((property) => (
                <IssuePropertySelect
                  disabled={savingField}
                  editable={isAdmin}
                  field={property.field}
                  key={property.field}
                  label={t(property.label)}
                  onSave={(value) => saveIssueFields({ [property.field]: value })}
                  options={property.query.data ?? []}
                  reference={property.query}
                  value={issue[property.field] ?? ""}
                />
              ))}
              <IssuePropertySelect
                disabled={savingField}
                displayValue={formatIssueReference(parentIssue) ?? t("issue.unassigned")}
                editable={isAdmin}
                field="parentIssueId"
                label={t("issue.field.parentIssue")}
                onSave={(parentIssueId) => saveIssueFields({ parentIssueId })}
                options={(references.issues.data ?? [])
                  .filter((candidate) => candidate.id !== issue.id)
                  .map((candidate) => ({
                    id: candidate.id,
                    name: `${candidate.key} · ${candidate.title}`,
                  }))}
                reference={references.issues}
                value={issue.parentIssueId ?? ""}
              />
              <IssueLabelEditor
                disabled={savingField}
                editable={isAdmin}
                labels={references.labels.data ?? []}
                onSave={(labelIds) => saveIssueFields({ labelIds })}
                reference={references.labels}
                value={issue.labels}
              />
            </div>
          </details>
          <div className="issue-property-dates">
            <KeyValueRow label={t("issue.detail.created")} value={formatDate(issue.createdAt)} />
            <KeyValueRow label={t("issue.field.updated")} value={formatDate(issue.updatedAt)} />
          </div>
        </aside>
        <div className="issue-workspace">
          <section className="issue-activity" aria-label={t("issue.timeline")}>
            <PanelHeader
              icon={<History size={16} />}
              label={t("issue.timeline")}
              meta={String(
                detail.activity.filter((event) => event.type !== "comment.created").length +
                  detail.comments.length,
              )}
            />
            <Tabs value={activityTab} onValueChange={setActivityTab}>
              <TabsList aria-label={t("issue.detail.activityFilter")} variant="line">
                <TabsTrigger value="all">{t("issue.views.all")}</TabsTrigger>
                <TabsTrigger value="comments">{t("issue.comments")}</TabsTrigger>
                <TabsTrigger value="changes">{t("issue.detail.changes")}</TabsTrigger>
              </TabsList>
              <TabsContent value={activityTab}>
                <div className="issue-activity-feed">
                  {feed.length ? (
                    feed.map((entry) =>
                      entry.kind === "comment" ? (
                        <article className="issue-conversation-entry" key={entry.data.id}>
                          <Avatar aria-hidden="true" size="sm">
                            {actorInitial(
                              entry.data.actorType,
                              entry.data.actorId,
                              entry.data.actorDisplayName,
                            )}
                          </Avatar>
                          <div className="issue-conversation-content">
                            <header>
                              <strong>
                                {actorDisplayName(
                                  entry.data.actorType,
                                  entry.data.actorId,
                                  entry.data.actorDisplayName,
                                )}
                              </strong>
                              <time dateTime={entry.data.createdAt}>
                                {formatDate(entry.data.createdAt)}
                              </time>
                            </header>
                            <p>{entry.data.body}</p>
                          </div>
                        </article>
                      ) : (
                        <article
                          className="issue-change-entry"
                          data-event-type={entry.data.type}
                          key={entry.data.id}
                        >
                          <History aria-hidden="true" size={14} />
                          <div>
                            <strong>
                              {actorDisplayName(
                                entry.data.actorType,
                                entry.data.actorId,
                                entry.data.actorDisplayName,
                              )}
                            </strong>{" "}
                            {activityLabel(entry.data.type, t)}
                          </div>
                          <time dateTime={entry.data.createdAt}>
                            {formatDate(entry.data.createdAt)}
                          </time>
                        </article>
                      ),
                    )
                  ) : (
                    <p className="muted-copy">
                      {t(
                        activityTab === "comments"
                          ? "issue.detail.noComments"
                          : "issue.detail.noActivity",
                      )}
                    </p>
                  )}
                </div>
              </TabsContent>
            </Tabs>
            <form className="comment-form issue-comment-composer" onSubmit={onCommentSubmit}>
              <Avatar aria-hidden="true" size="sm">
                {auth.user?.name?.slice(0, 1) ?? "Q"}
              </Avatar>
              <MentionTextarea
                ariaLabel={t("issue.comment.placeholder")}
                noResultsLabel={t("issue.userAutocomplete.noResults")}
                placeholder={t("issue.comment.placeholder")}
                users={references.users.data ?? []}
                value={comment}
                onValueChange={setComment}
              />
              <CommentSubmitButton disabled={comment.trim().length === 0} />
            </form>
          </section>
          <div className="issue-resources">
            <details
              className="issue-resource-block"
              open={Boolean(latestRun) || issue.assigneeAgentId !== null}
            >
              <summary>
                <span className="icon-title">
                  <Bot aria-hidden="true" size={16} />
                  {t("issue.agentPanel")}
                </span>
                <Badge tone={latestRun?.status === "completed" ? "success" : "neutral"}>
                  {latestRun?.status.replaceAll("_", " ") ?? t("agents.notStarted")}
                </Badge>
                <ChevronDown aria-hidden="true" size={14} />
              </summary>
              <div className="issue-resource-body">
                <div className="metadata-stack">
                  <KeyValueRow
                    label={t("agents.goal")}
                    value={latestRun?.goal ?? t("agents.noGoal")}
                  />
                  <KeyValueRow
                    label={t("agents.status")}
                    value={latestRun?.status.replaceAll("_", " ") ?? t("agents.notStarted")}
                  />
                </div>
                <div className="agent-panel-actions">
                  <Input
                    aria-label={t("agents.goal")}
                    id="agent-run-goal"
                    placeholder={t("agents.goal")}
                    value={runGoal}
                    onChange={(event) => setRunGoal(event.currentTarget.value)}
                  />
                  <Button
                    disabled={issue.assigneeAgentId === null || !runGoal.trim()}
                    type="button"
                    onClick={() => void startRun()}
                  >
                    {t("agents.startRun")}
                  </Button>
                </div>
                {latestRun ? (
                  <div className="agent-action-grid">
                    <Input
                      aria-label={t("agents.message")}
                      placeholder={t("agents.message")}
                      value={runNote}
                      onChange={(event) => setRunNote(event.currentTarget.value)}
                    />
                    {latestRunActions.map((action) => (
                      <Button
                        key={action.id}
                        variant={action.id === "fail" ? "danger" : "secondary"}
                        onClick={() =>
                          void updateLatestRun(
                            action.targetStatus as Parameters<typeof updateLatestRun>[0],
                          )
                        }
                      >
                        <AgentRunActionIcon actionId={action.id} />
                        {t(action.labelKey)}
                      </Button>
                    ))}
                    <Button variant="secondary" onClick={() => void attachRunArtifact()}>
                      {t("agents.attachArtifact")}
                    </Button>
                  </div>
                ) : null}
              </div>
            </details>
            <details className="issue-resource-block" open={detail.artifacts.length > 0}>
              <summary>
                <span className="icon-title">
                  <Database aria-hidden="true" size={16} />
                  {t("issue.artifacts")}
                </span>
                <span className="issue-resource-count">{detail.artifacts.length}</span>
                <ChevronDown aria-hidden="true" size={14} />
              </summary>
              <div className="issue-resource-body">
                <div className="side-list">
                  {detail.artifacts.length === 0 ? (
                    <p className="muted-copy">{t("issue.artifacts.empty")}</p>
                  ) : null}
                  {detail.artifacts.map((artifact) => (
                    <span className="side-list-item" key={artifact.id}>
                      <span>{artifact.title}</span>
                      <time>{formatDate(artifact.createdAt)}</time>
                      {artifact.body ? <small>{artifact.body}</small> : null}
                    </span>
                  ))}
                </div>
              </div>
            </details>
            <details className="issue-resource-block" open={detail.githubPullRequests.length > 0}>
              <summary>
                <span className="icon-title">
                  <GitPullRequest aria-hidden="true" size={16} />
                  {t("issue.githubPanel")}
                </span>
                <span className="issue-resource-count">{detail.githubPullRequests.length}</span>
                <ChevronDown aria-hidden="true" size={14} />
              </summary>
              <div className="issue-resource-body">
                <div className="work-form">
                  <div className="agent-panel-actions">
                    <Input
                      aria-label={t("github.branchSuggestion")}
                      readOnly
                      value={branchName}
                      placeholder={t("github.branchSuggestion")}
                    />
                    <Button type="button" variant="secondary" onClick={() => void suggestBranch()}>
                      {t("github.suggestBranch")}
                    </Button>
                  </div>
                  <div className="agent-panel-actions">
                    <Input
                      aria-describedby="github-pr-url-error"
                      aria-invalid={prTouched && prUrlErrorKey !== null ? "true" : undefined}
                      aria-label={t("github.prUrl")}
                      placeholder={t("github.prUrl")}
                      type="url"
                      value={prUrl}
                      onBlur={() => setPrTouched(true)}
                      onChange={(event) => setPrUrl(event.currentTarget.value)}
                    />
                    <Button
                      disabled={!canLinkPullRequest}
                      type="button"
                      onClick={() => void linkPr()}
                    >
                      {t("github.linkPr")}
                    </Button>
                  </div>
                  {prTouched && prUrlErrorKey !== null ? (
                    <p className="error-text" id="github-pr-url-error">
                      {t(prUrlErrorKey)}
                    </p>
                  ) : null}
                  <div className="side-list">
                    {detail.githubPullRequests.length === 0 ? (
                      <p className="muted-copy">{t("issue.githubPlaceholder")}</p>
                    ) : null}
                    {detail.githubPullRequests.map((pullRequest) => (
                      <a className="side-list-item" href={pullRequest.url} key={pullRequest.id}>
                        <ExternalLink size={14} />
                        <span>{pullRequest.repository}</span>
                        <time>{pullRequest.state}</time>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </details>
          </div>
          {isAdmin ? (
            <div className="issue-admin-actions">
              <Button
                size="sm"
                variant={deleteArmed ? "danger" : "ghost"}
                onClick={() => void onDeleteIssue()}
              >
                {deleteArmed ? t("issue.action.confirmDelete") : t("issue.action.delete")}
              </Button>
              {deleteArmed ? (
                <Button size="sm" variant="ghost" onClick={() => setDeleteArmed(false)}>
                  {t("issue.action.cancelDelete")}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
      {isAdmin ? (
        <EditIssueModal
          issue={issue}
          open={editing}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setConfirmedIssue(null);
            return detailQuery.refetch();
          }}
        />
      ) : null}
    </section>
  );
}
